import merge from "lodash.merge";
import { Col, Row } from "react-bootstrap";
import { Controller, useFieldArray, useForm, useWatch } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import { ProposedProjectController } from "services/proposedProject/controller";
import { useAccordionWizard } from "stories";
import { AccordionForm } from "stories/components/accordionForm";
import { RadioGroupList } from "stories/components/radioGroup/RadioGroup";
import { useToast } from "utils/customHooks/useToast";
import { removeEmptyValues } from "../../../loanDetails/elements/borrower/LoanDetailsBorrower";
import { staticEntities } from "../constants";
import LenderItem from "./lenderItem/LenderItem";

const proposedProjectController = ProposedProjectController();

const getLenderModel = () => ({
  name: "",
  initial_loan_amount: "",
  principle_outstanding: "",
  loan_type: "",
  mortgage_position: "",
  loan_structure: "interest only",
  amortization_period: {
    count: null,
    unit: "month",
  },
  being_paid_off: false,
  mortgage_payout_amount: "",
  discharge_fee_incurred: false,
  discharge_fee: "",
  payment_frequency: "monthly",
  loan_term: {
    count: null,
    unit: "month",
  },
  interest_rate: "",
  interest_rate_type: "fixed",
  premium: "",
  interest_reserve: false,
  interest_reserve_amount: "",
  maturity_date: "",
  current_debt_service: "",
  additional_notes: "",
});
export const FinancingQueryKey = "financing";
const Financing = ({ opportunity }) => {
  const form = useForm({
    defaultValues: {
      has_debt: false,
      lenders: [getLenderModel()],
    },
  });
  const {
    fields: lenders,
    append: addLender,
    remove: deleteLender,
  } = useFieldArray({
    control: form.control,
    name: "lenders",
  });
  const hasLender = useWatch({ control: form.control, name: "has_debt" });
  const params = useParams();
  const queryKey = [FinancingQueryKey, { id: params.id }];
  const { showToast } = useToast();
  const { next } = useAccordionWizard();
  const queryClient = useQueryClient();

  const { isLoading: fetchLoading } = useQuery({
    queryKey,
    queryFn: proposedProjectController.getExistingFinancing.bind(
      null,
      params.id
    ),
    onSuccess: (data) => {
      if (data.lenders.length > 0) {
        for (let i = 0; i < data.lenders.length; i++) {
          data.lenders[i] = merge(getLenderModel(), data.lenders[i]);
        }
      }
      form.reset(merge(form.getValues(), data));
    },
  });

  const handleFinancingSubmit = (body) => {
    body = removeEmptyValues(body);
    body.opportunityId = params.id;
    return proposedProjectController.updateExistingFinancing(body);
  };

  const { mutate, isLoading: postLoading } = useMutation(
    handleFinancingSubmit,
    {
      onSuccess: () => {
        showToast("Financing is Updated!", "success");
        next();
        queryClient.invalidateQueries(queryKey);
      },
    }
  );
  const handleAddLender = () => {
    addLender(getLenderModel());
  };
  return (
    <AccordionForm
      accordionKey="existing-financing"
      accordionTitle="Existing Financing"
      form={form}
      isPostLoading={postLoading}
      isPreLoading={fetchLoading}
      onSubmit={mutate}
    >
      <Row className={"mb-4"}>
        <Col xs={4}>
          <Controller
            name="has_debt"
            control={form.control}
            render={({ field }) => {
              return (
                <RadioGroupList
                  label={"Is there any current debt on the property?"}
                  list={staticEntities.booleanList}
                  fullWidth
                  {...field}
                />
              );
            }}
          />
        </Col>
      </Row>
      {hasLender && (
        <>
          {lenders.map((item, index) => (
            <LenderItem
              key={item.id}
              index={index}
              onDelete={() => deleteLender(index)}
              opportunity={opportunity}
            />
          ))}
          <div
            className={"fw-bold text-primary my-3 d-inline-block"}
            role={"button"}
            onClick={handleAddLender}
          >
            + Add Lender
          </div>
        </>
      )}
    </AccordionForm>
  );
};

export default Financing;
